import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Chip, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DataTable from '@ui/components/common/DataTable';
import { between } from '@ui/style/style';
import ButtonDropdown from '@ui/components/ButtonDropdown';
import { Calendar2 } from 'iconsax-react';
import { useAppDispatch } from '@application/store/useStore';
import { getAllExternalRelationRecentEventsAsync } from '@application/features/externalRelations/externalRelationsThunk';
import { useSelector } from 'react-redux';
// // Placeholder data
// const mockEventsAttended: EventAttended[] = [
//   {
//     id: '1',
//     eventName: 'F1 Grand Prix Event',
//     date: 'Jun 15, 2025',
//     interests: ['Racing', 'Sports', 'Entertainment', 'Networking'],
//     eventType: 'Internal'
//   },
//   {
//     id: '2',
//     eventName: 'Business Networking Conference',
//     date: 'May 20, 2025',
//     interests: ['Business', 'Networking', 'Professional Development'],
//     eventType: 'Sponsorship'
//   },
//   {
//     id: '3',
//     eventName: 'Technology Innovation Summit',
//     date: 'Apr 10, 2025',
//     interests: ['Technology', 'Innovation', 'Business', 'Networking'],
//     eventType: 'Internal'
//   },
//   {
//     id: '4',
//     eventName: 'Charity Gala Dinner',
//     date: 'Mar 25, 2025',
//     interests: ['Charity', 'Social', 'Networking'],
//     eventType: 'Sponsorship'
//   },
//   {
//     id: '5',
//     eventName: 'Industry Awards Ceremony',
//     date: 'Feb 18, 2025',
//     interests: ['Awards', 'Recognition', 'Networking', 'Business'],
//     eventType: 'Internal'
//   }
// ];
const getDateRangeOptions = (t) => [
    { id: 'all', text: t('people.dateRangeOptions.all') },
    { id: '7days', text: t('people.dateRangeOptions.7days') },
    { id: '10days', text: t('people.dateRangeOptions.10days') },
    { id: '30days', text: t('people.dateRangeOptions.30days') },
    { id: '6months', text: t('people.dateRangeOptions.6months') },
    { id: '1year', text: t('people.dateRangeOptions.1year') },
    { id: 'custom', text: t('people.dateRangeOptions.custom') }
];
const EventsAttendedTab = ({ relationId }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [selectedRangeOption, setSelectedRangeOption] = React.useState('all');
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const { externalRelationRecentEvent } = useSelector((state) => state.externalRelations);
    const dispatch = useAppDispatch();
    const predefinedDateRanges = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const sevenDaysWindowStart = new Date(yesterday);
        sevenDaysWindowStart.setDate(sevenDaysWindowStart.getDate() - 6);
        const tenDaysWindowStart = new Date(yesterday);
        tenDaysWindowStart.setDate(tenDaysWindowStart.getDate() - 9);
        const thirtyDaysWindowStart = new Date(yesterday);
        thirtyDaysWindowStart.setDate(thirtyDaysWindowStart.getDate() - 29);
        const sixMonthsWindowStart = new Date(yesterday);
        sixMonthsWindowStart.setMonth(sixMonthsWindowStart.getMonth() - 6);
        const oneYearWindowStart = new Date(yesterday);
        oneYearWindowStart.setFullYear(oneYearWindowStart.getFullYear() - 1);
        return {
            '7days': { startDate: sevenDaysWindowStart.toISOString(), endDate: yesterday.toISOString() },
            '10days': { startDate: tenDaysWindowStart.toISOString(), endDate: yesterday.toISOString() },
            '30days': {
                startDate: thirtyDaysWindowStart.toISOString(),
                endDate: yesterday.toISOString()
            },
            '6months': {
                startDate: sixMonthsWindowStart.toISOString(),
                endDate: yesterday.toISOString()
            },
            '1year': { startDate: oneYearWindowStart.toISOString(), endDate: yesterday.toISOString() }
        };
    }, []);
    const resolvedDateRange = useMemo(() => {
        if (selectedRangeOption === 'all') {
            return { startDate: undefined, endDate: undefined };
        }
        if (selectedRangeOption === 'custom') {
            return {
                startDate: selectedDateRange?.startDate,
                endDate: selectedDateRange?.endDate
            };
        }
        const range = predefinedDateRanges[selectedRangeOption];
        return {
            startDate: range?.startDate,
            endDate: range?.endDate
        };
    }, [predefinedDateRanges, selectedDateRange, selectedRangeOption]);
    useEffect(() => {
        if (relationId) {
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                relationId: relationId,
                startDate: resolvedDateRange.startDate,
                endDate: resolvedDateRange.endDate
            };
            dispatch(getAllExternalRelationRecentEventsAsync(request));
        }
    }, [
        dispatch,
        paginationModel.page,
        paginationModel.pageSize,
        relationId,
        resolvedDateRange.endDate,
        resolvedDateRange.startDate
    ]);
    const handleDateRangeSelection = (optionId) => {
        setSelectedRangeOption(optionId);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        if (optionId === 'all') {
            setSelectedDateRange(null);
            return;
        }
        if (optionId === 'custom') {
            return;
        }
        const range = predefinedDateRanges[optionId];
        if (range) {
            setSelectedDateRange(range);
        }
    };
    const handleCustomDateRangeChange = (range) => {
        if (range) {
            setSelectedDateRange(range);
            setSelectedRangeOption('custom');
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }
    };
    const columns = [
        {
            key: 'eventName',
            label: t('events.eventName') || 'Event Name',
            width: '25%',
            render: (row) => (_jsx(Typography, { variant: 'body2', children: row.eventName }))
        },
        {
            key: 'date',
            label: t('events.date') || 'Date',
            width: '25%',
            render: (row) => {
                if (!row.eventDate)
                    return '-';
                const eventDate = new Date(row.eventDate);
                if (isNaN(eventDate.getTime()))
                    return '-';
                return (_jsxs(Typography, { variant: 'body2', children: [eventDate.getDate(), "/", eventDate.getMonth() + 1, "/", eventDate.getFullYear()] }));
            }
        },
        {
            key: 'interests',
            label: t('settings.interests') || 'Interests',
            width: '25%',
            align: 'center',
            render: (row) => (_jsx(Tooltip, { title: _jsx(Box, { sx: { p: 0.5 }, children: row.interests.map((interest, index) => (_jsx(Typography, { variant: 'body2', sx: { py: 0.25 }, children: interest.title }, index))) }), arrow: true, placement: 'bottom', children: _jsx(Chip, { label: String(row.interests.length).padStart(2, '0'), size: 'small', sx: { fontWeight: 400, borderRadius: '500px', cursor: 'pointer' }, variant: 'soft', color: 'default' }) }))
        },
        {
            key: 'eventType',
            label: t('events.eventType') || 'Event Type',
            width: '25%',
            render: (row) => (_jsx(Chip, { color: row.eventType === 'Internal' ? 'success' : 'warning', size: 'medium', label: row.eventType === 'Internal' ? t('events.internal') || 'Internal' : row.eventType, sx: { fontWeight: 400, borderRadius: '8px' }, variant: 'soft' }))
        }
    ];
    return (_jsxs(Box, { sx: {
            backgroundColor: theme.palette.common.whiteTransparent,
            height: '100%'
        }, children: [_jsxs(Box, { sx: { ...between, mb: 1.5 }, children: [_jsx(Typography, { variant: 'h4', color: theme.palette.text.primary, children: t('people.recentEvents') || 'Recent Events' }), _jsx(ButtonDropdown, { label: t('sponsorships.activityTab.monthFilter') || 'Select Date Range', options: getDateRangeOptions(t), variant: 'outlined', size: 'large', color: 'primary', startIcon: _jsx(Calendar2, { variant: 'Outline' }), selectedItem: selectedRangeOption, onSelectionChange: handleDateRangeSelection, onDateRangeChange: handleCustomDateRangeChange, customDateRange: selectedDateRange, enableDateRange: true })] }), _jsx(DataTable, { data: externalRelationRecentEvent?.items ? externalRelationRecentEvent.items : [], columns: columns, getRowId: (row) => row.eventId, showSearchFilter: false, pagination: true, page: paginationModel.page, rowsPerPage: paginationModel.pageSize, totalCount: externalRelationRecentEvent?.totalCount, onPageChange: (page) => setPaginationModel((prev) => ({ ...prev, page })), onRowsPerPageChange: (rowsPerPage) => setPaginationModel((prev) => ({ ...prev, pageSize: rowsPerPage })), rowsPerPageOptions: [5, 10, 25, 50], emptyMessage: t('people.noEventsAttended') || 'No events attended' })] }));
};
export default EventsAttendedTab;
